import '@spotted-zebra-uk/ui-components/dist/sz-theme/_globalStyles.scss';
import { AuthenticationWrapper } from 'components/organisms/AuthenticationWrapper/AuthenticationWrapper';
import BaseCompanyThemeProvider from 'components/organisms/BaseCompanyThemeProvider/BaseCompanyThemeProvider';
import { FocusOnRouteChange } from 'components/organisms/FocusOnRouteChange/FocusOnRouteChange';
import IntercomLiveChatGate from 'components/organisms/IntercomLiveChatGate/IntercomLiveChatGate';
import LocalizationWrapper from 'components/organisms/LocalizationWrapper/LocalizationWrapper';
import UserWrapper from 'components/organisms/UserWrapper/UserWrapper';
import client from 'graphql/client';
import { History } from 'history';
import { memo, useEffect, useLayoutEffect, useState } from 'react';
import { LiveAnnouncer } from 'react-aria-live';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouterProps as NativeBrowserRouterProps,
  NavigationType,
  Router,
} from 'react-router-dom';
import ModalView from 'views/ModalView/ModalView';
import history from '@/config/history';
import { SupportEmailProvider } from '@/contexts/SupportEmail/SupportEmailContext';
import AppSwitch from '@/navigation/AppSwitch/AppSwitch';
import PublicSchedulingSwitch, {
  isPublicSchedulingRoute,
} from '@/navigation/PublicSchedulingSwitch/PublicSchedulingSwitch';
import { ApolloProvider } from '@apollo/client';
import { ThemeProvider } from '@spotted-zebra-uk/ui';
import {
  useAccessibilityKeyboardDetection,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { HotjarProvider } from './providers/HotjarProvider';

export interface BrowserRouterProps extends Omit<
  NativeBrowserRouterProps,
  'window'
> {
  history: History;
}

export const BrowserRouter = memo(
  ({ history, ...rest }: BrowserRouterProps) => {
    const [state, setState] = useState({
      action: history.action,
      location: history.location,
    });

    useLayoutEffect(() => {
      history?.listen(({ location, action }) => {
        setState({ location, action });
      });
    }, [history]);

    return (
      <Router
        {...rest}
        navigator={history}
        location={state.location}
        navigationType={state.action as NavigationType}
      />
    );
  }
);

function App() {
  const { i18n } = useTranslation();
  const { notificationContainer } = useNotification();

  useAccessibilityKeyboardDetection();

  const [isSafariFixIframeVisible, setSafariFixIframeVisibility] =
    useState(true);

  useEffect(() => {
    const timeoutRef = setTimeout(
      () => setSafariFixIframeVisibility(false),
      5000
    );

    return () => {
      clearTimeout(timeoutRef);
    };
  });

  // The public scheduling namespace bypasses the authenticated provider tree
  // (auth, user, localization, theme, analytics) — same pattern as the company
  // client's standalone interview-guide PDF route.
  if (isPublicSchedulingRoute(window.location.pathname)) {
    return (
      <BrowserRouter history={history}>
        <PublicSchedulingSwitch />
      </BrowserRouter>
    );
  }

  return (
    <LiveAnnouncer>
      {notificationContainer()}

      {isSafariFixIframeVisible && (
        <iframe
          src="https://tptests.com/api/candidate/safari"
          height="0"
          width="0"
          title="tp-safari-issue-fix"
          style={{ position: 'absolute' }}
        />
      )}
      <HelmetProvider>
        <Helmet>
          {/* useTranslation re-renders on language change, keeping this current */}
          <html lang={i18n.language || 'en'} />
          <title>Spotted Zebra Candidate</title>
        </Helmet>
        <ApolloProvider client={client}>
          <AuthenticationWrapper>
            <LocalizationWrapper>
              <UserWrapper>
                <HotjarProvider>
                  <ThemeProvider>
                    <BaseCompanyThemeProvider>
                      <BrowserRouter history={history}>
                        <SupportEmailProvider>
                          <IntercomLiveChatGate />
                          <FocusOnRouteChange>
                            <AppSwitch />
                          </FocusOnRouteChange>
                          <ModalView />
                        </SupportEmailProvider>
                      </BrowserRouter>
                    </BaseCompanyThemeProvider>
                  </ThemeProvider>
                </HotjarProvider>
              </UserWrapper>
            </LocalizationWrapper>
          </AuthenticationWrapper>
        </ApolloProvider>
      </HelmetProvider>
    </LiveAnnouncer>
  );
}

export default App;
